import { clsx } from "clsx";
import { useMemo, useRef } from "react";
import { useHover } from "react-aria";
import {
  Card,
  CardRowSimple,
  ColorButton,
  DialogComponent,
  type DraggableItem,
  HStack,
  Item,
  MenuOptionItem,
  Modal,
  ReorderableList,
  ReorderableListItem,
  Select,
  Spacer,
  SymbolIcon,
  TriggerButton,
  TriggerButtonSize,
  VStack,
} from "widgets";
import { useOverlay } from "widgets/hooks";

import { TrackTextEnum } from "../../openapi";
import { syntenyViewerDefaultTracks } from "../../settings.ts";
import { useGenomeViewerStore } from "../../store/store.ts";
import { type Track, type TrackText } from "../../types.ts";
import styleGroup from "./TrackGroupsList.module.css";
import style from "./TracksList.module.css";

interface TracksListProps {
  tracks: Track[];
  groupId: string;
}

type DraggableTracks = {
  // id and name are needed for react-aria-components
  id: string;
  name: string;
} & Track;

export const TrackList = ({ groupId, tracks }: TracksListProps) => {
  const reorderTracksInGroup = useGenomeViewerStore((state) => state.reorderTracksInGroup);

  const trackList: DraggableTracks[] = useMemo(() => {
    return tracks.map((track) => ({
      id: track.trackId,
      name: track.trackType, // This is the track label, like protein, dna, etc. ;-)
      ...track,
    }));
  }, [tracks]);

  // Handle reordering
  const handleReorder = (items: DraggableItem<DraggableTracks>[]) => {
    reorderTracksInGroup(
      groupId,
      items.map((item) => item.trackId),
    );
  };

  // Render drag preview
  const renderDragPreview = (track: Track) => <TrackListItemDragPreview track={track} key={`track_${track.trackId}`} />;

  return (
    <ReorderableList<DraggableTracks>
      items={trackList}
      onReorder={handleReorder}
      renderDragPreview={renderDragPreview}
      key="reorderable-tracks"
    >
      {(track) => (
        <ReorderableListItem id={track.trackId} key={`track-list-item-${track.trackId}`}>
          <TrackListItem track={track} key={`track_${track.trackId}`} />
        </ReorderableListItem>
      )}
    </ReorderableList>
  );
};

interface TrackListItemProps {
  track: Track;
}

const TrackListItem = ({ track }: TrackListItemProps) => {
  const trackGroup = useGenomeViewerStore((state) => state.trackGroups.get(track.groupId));
  const updateTrack = useGenomeViewerStore((state) => state.updateTrack);
  const isDialogOpenRef = useRef(false);

  const { hoverProps, isHovered } = useHover({});
  const { overlayProps, state } = useOverlay({
    onOpenChange(isOpen) {
      isDialogOpenRef.current = isOpen;
    },
  });

  const isConfigurable = syntenyViewerDefaultTracks.find((t) => t.type === track.trackType)?.isConfigurable ?? true;
  const isHover = isDialogOpenRef.current || isHovered;

  if (trackGroup == undefined) {
    return null;
  }

  return (
    <>
      <VStack>
        <HStack className={clsx(styleGroup.item, { [styleGroup.item_active]: isHover })} domProps={hoverProps}>
          <SymbolIcon icon="crop_16_9" />
          <div className={style.track}>
            {syntenyViewerDefaultTracks.find((t) => t.type === track.trackType)?.name ?? track.subType?.name}
          </div>
          <Spacer />
          <HStack spacing={0}>
            {isHover && !!isConfigurable ? (
              <TriggerButton
                icon={"settings"}
                onPress={state.open}
                label="Customize Track"
                size={TriggerButtonSize.Small}
              />
            ) : null}
            <TriggerButton
              onPress={() => updateTrack(track.trackId, { isVisible: !track.isVisible })}
              icon={track.isVisible ? "visibility" : "visibility_off"}
              label="Toggle Track Visibility"
              size={TriggerButtonSize.Small}
            />
          </HStack>
        </HStack>
      </VStack>
      <Modal state={state} {...overlayProps}>
        <DialogComponent
          data-testid="track-customization"
          contentStyle={{ padding: 0, overflowY: "hidden" }}
          mainButton={{
            label: "Close",
            onPress: () => {
              state.close();
            },
          }}
          asPanelHeader
          title="Track Customization"
        >
          <Card noBorder>
            <CardRowSimple>
              <Select
                icon={"text_format"}
                label={"Text Format"}
                isRequired
                onSelectionChange={(key) => {
                  updateTrack(track.trackId, { text: String(key) as TrackText });
                }}
                selectedKey={track.text}
              >
                <Item key={TrackTextEnum.INNER} aria-label="Inner">
                  <MenuOptionItem title={"Inner Text"} />
                </Item>
                <Item key={TrackTextEnum.OUTER} aria-label="Outer">
                  <MenuOptionItem title={"Outer Text"} />
                </Item>
                <Item key={TrackTextEnum.NONE} aria-label="None">
                  <MenuOptionItem title={"No Text"} />
                </Item>
              </Select>
            </CardRowSimple>
            <CardRowSimple label={"Color of Shapes"}>
              <ColorButton
                defaultColor={track.color}
                onApply={(color) => {
                  updateTrack(track.trackId, { color });
                }}
              />
            </CardRowSimple>
          </Card>
        </DialogComponent>
      </Modal>
    </>
  );
};

const TrackListItemDragPreview = ({ track }: TrackListItemProps) => {
  return (
    <VStack>
      <HStack className={clsx(styleGroup.item)}>
        <SymbolIcon icon="crop_16_9" />
        <div className={style.track}>
          {syntenyViewerDefaultTracks.find((t) => t.type === track.trackType)?.name ?? track.subType?.name}
        </div>
      </HStack>
    </VStack>
  );
};
